import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { format } from "date-fns";
import { toast } from "sonner";
import { notifyResearch } from "@/hooks/useNotifications";

export default function ResearchComments({ itemId, itemTitle, authorId }: { itemId: string; itemTitle: string; authorId: string | null }) {
  const qc = useQueryClient();
  const [body, setBody] = useState("");

  const { data: comments = [] } = useQuery({
    queryKey: ["research_comments", itemId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("research_comments")
        .select("*")
        .eq("item_id", itemId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

  const add = useMutation({
    mutationFn: async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) throw new Error("Not signed in");
      const { error } = await supabase.from("research_comments").insert({
        item_id: itemId, author_id: u.user.id, body,
      });
      if (error) throw error;
      // Notify the author (if not self)
      if (authorId && authorId !== u.user.id) {
        await notifyResearch({
          user_ids: [authorId],
          category: "research.comment",
          title: `New comment on "${itemTitle}"`,
          message: body.slice(0, 140),
          link_url: `/partner/research/${itemId}`,
          data: { item_id: itemId },
        });
      }
    },
    onSuccess: () => {
      setBody("");
      qc.invalidateQueries({ queryKey: ["research_comments", itemId] });
      toast.success("Comment added");
    },
    onError: (e: any) => toast.error(e.message),
  });

  const resolve = useMutation({
    mutationFn: async ({ id, resolved }: { id: string; resolved: boolean }) => {
      const { error } = await supabase.from("research_comments").update({ resolved }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["research_comments", itemId] }),
  });

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="font-semibold text-sm">Comments ({comments.length})</div>
        <div className="space-y-2">
          {comments.length === 0 && <div className="text-sm text-muted-foreground">No comments yet.</div>}
          {comments.map((c: any) => (
            <div key={c.id} className={`text-sm border rounded p-2 ${c.resolved ? "opacity-60" : ""}`}>
              <div className="text-[11px] text-muted-foreground mb-1">
                {format(new Date(c.created_at), "dd/MM/yyyy HH:mm")}
              </div>
              <div className="whitespace-pre-wrap">{c.body}</div>
              <div className="mt-1">
                <Button size="sm" variant="ghost" onClick={() => resolve.mutate({ id: c.id, resolved: !c.resolved })}>
                  {c.resolved ? "Reopen" : "Resolve"}
                </Button>
              </div>
            </div>
          ))}
        </div>
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Add a comment…" rows={2} />
        <Button size="sm" onClick={() => add.mutate()} disabled={!body.trim() || add.isPending}>
          {add.isPending ? "Posting…" : "Post comment"}
        </Button>
      </CardContent>
    </Card>
  );
}
